import { PureComponent } from 'react'
import { setupECI13n } from '@vzmi/ec-react-i13n'
import { PROPOSAL_TYPE, PROPOSAL_PHASE_TYPE } from '@vzmi/shp_shared_compo/constants/proposal'
import siteCommon from '../../../../conf/siteCommon'
import { retrieveRouteByLocation, sendI13nPageView, retrievePageI13n, booleanToNum } from '../../../lib/i13nUtil'
import { getLogger } from '../../../lib/browserLogger'
import {
  PAYLOAD_DIFF_TYPE
} from '../Proposal/ProductListingBaseForm/ListingModel/ExtraModelTable/helper.js'
import _ from 'lodash'
/**
 * i13n component wrapper
 * setup i13n, and create a top level component wrapper
 */
export class ECI13nWrapper extends PureComponent {
  constructor (props) {
    super(props)
    const rootComponent = props.component
    if (!rootComponent) {
      console.error('I13nWrapper cannot be created without child component.')
      return rootComponent
    }
    if (!props.env) {
      console.error('I13nWrapper: props.env not found, please check your container setting again.')
    }

    const isProductionEnv = props.env === 'production'
    /**
     * 非 Production 環境用 gaDev 設定
     */
    const i13nGaConfig = _.get(
      siteCommon,
      isProductionEnv
        ? 'ga'
        : 'gaDev',
      {}
    )

    const i13nRapidConfig = _.get(
      siteCommon,
      'rapid',
      {}
    )

    this.component = setupECI13n(
      rootComponent,
      {
        property: 'shopping',
        rapid: i13nRapidConfig,
        ga: i13nGaConfig,
        gtm: {
          containerId: isProductionEnv
            ? 'GTM-K5MFBGR'
            : 'GTM-5HNBDT3'
        }
      })
  }

  render () {
    const Component = this.component
    return <Component {...this.props} />
  }
}

/**
 * wrap in our own i13n methods,
 * depend on ECI13nWrapper
 */
const logger = getLogger({ name: 'withI13nMethods' })
const withI13nMethods = WrappedComponent => {
  return class extends PureComponent {
    sendI13nPageView = (i13nData) => {
      this.props.actions.setI13nData(i13nData)
      if (!global.window && !window._reactI13nInstance) {
        logger.warn('sendI13nPageView, please make sure you have wrapped your top component with withI13nWrapper!')
        return
      }
      sendI13nPageView({
        ...i13nData,
        gtmDataLayer: {
          content_group: i13nData.documentGroup,
          page_type: i13nData.subType,
          supplier_id: i13nData.cat1Name,
          supplier_name: i13nData.cat2Name,
          listing_count: i13nData.cat3Name,
          new_product: i13nData.cat1Id, // 即原本dimension9
          new_spec: i13nData.cat2Id, // dimension10
          update_accessory: i13nData.cat3Id, // dimension11
          reservation: i13nData.cat4Id // dimension12
        }
      })
    }

    sendI13nPageViewByPageKey = (pageKey, i13nPageConf) => {
      const { i13nData: i13nDataFromProps, page, reduxFormState } = this.props
      const lastI13nData = i13nDataFromProps
      const gaDynamicDataParams = { page }
      let i13nData = retrievePageI13n({ pageKey, ...gaDynamicDataParams }, i13nPageConf)

      if (_.isEqual(lastI13nData, i13nData)) {
        return
      }
      // 賣場屬性調整確認頁額外增加dimension
      if (pageKey === `${PROPOSAL_TYPE.UPDATE_LISTING_MODEL}-${PROPOSAL_PHASE_TYPE.CONFIRM}`) {
        // dimension10(cat2Id) 是否無屬性轉單屬性
        const isUpdateFromLayer0 = _.flow(
          () => _.get(reduxFormState, 'values.listing.origLayer'),
          origLayer => origLayer === 0
        )()

        // dimension11(cat3Id) 是否調整贈品、加購品(any length of complimentaryDiff, selectComplimentaryDiff, additionalPurchasesDiff not 0)
        const isUpdateAccessory = Object.values(PAYLOAD_DIFF_TYPE).some(diffType => {
          return Object.keys(_.get(reduxFormState, `values.${diffType}`, {})).length
        })
        i13nData = {
          ...i13nData,
          cat2Id: booleanToNum(isUpdateFromLayer0),
          cat3Id: booleanToNum(isUpdateAccessory)
        }
      }
      logger.debug('sendI13nPageViewByPageKey pageKey:', pageKey, ' i13nData: ', i13nData)
      this.sendI13nPageView(i13nData)
    }

    sendI13nPageViewByRoute = (routeData, i13nPageConf) => {
      if (_.isEmpty(routeData)) {
        logger.warn('sendI13nPageViewByRoute routeData cannot be null to send i13n pageview!')
        return
      }
      const { i13nData: i13nDataFromProps, page } = this.props
      const lastI13nData = i13nDataFromProps
      const gaDynamicDataParams = { page }
      const i13nData = retrievePageI13n({ pageKey: routeData.pageKey, ...gaDynamicDataParams }, i13nPageConf)

      if (_.isEqual(lastI13nData, i13nData)) {
        return
      }
      logger.debug('sendI13nPageViewByRoute routeData:', routeData, 'i13nData: ', i13nData)
      this.sendI13nPageView(i13nData)
    }

    render () {
      const i13nMethods = {
        sendI13nPageView: this.sendI13nPageView,
        sendI13nPageViewByPageKey: this.sendI13nPageViewByPageKey,
        sendI13nPageViewByRoute: this.sendI13nPageViewByRoute
      }
      return (
        <WrappedComponent
          i13nMethods={i13nMethods}
          {...this.props}
        />
      )
    }
  }
}

/**
 * Contain a listener watching 'History'.
 * When 'history' changes, call sendI13nPageViewByRoute() with current routes.
 * Optional,
 * Depend on withI13nWrapper
 */
const i13nHistorylogger = getLogger({ name: 'withI13nHistoryListener' })
export const withI13nHistoryListener = WrappedComponent => {
  return class I13nHistoryListenerWrapped extends PureComponent {
    constructor (props) {
      super(props)
      this.historyListenerInstance = null
    }

    componentDidMount () {
      // setImmediate to skip direction from react-router
      setImmediate(() => {
        const {
          history,
          location,
          routes,
          i13nMethods: {
            sendI13nPageViewByRoute
          }
        } = this.props
        // send the first PV
        const currentRouteData = retrieveRouteByLocation({ location, routes })

        sendI13nPageViewByRoute(currentRouteData)
        // binding listener to history
        this.addHistoryListener(history, routes, sendI13nPageViewByRoute)
      })
    }

    componentWillUnmount () {
      this.removeHistoryListener()
    }

    removeHistoryListener = () => {
      /**
       * by calling instance to unlisten it
       * historyListenerInstance will be like this by now:
       * () {
          checkDOMListeners(-1);
          unlisten();
        }
       */
      if (this.historyListenerInstance) {
        this.historyListenerInstance()
      }
    }

    // 監聽history物件變動來判斷目前頁面所在並作出對應行為
    // ref: https://github.com/ReactTraining/react-router/issues/3554
    addHistoryListener = (history, routes, cb) => {
      if (!history) {
        i13nHistorylogger.error('[History Listener] - History binding failed, History is not defined.', 'input location: ', history, 'input routes: ', routes)
        return
      }
      // unlisten previous historyListener if any
      this.removeHistoryListener()

      this.historyListenerInstance = history.listen((nextLocation, action) => {
        const lastLocation = this.props.location
        // const lastHistory = this.props.history
        // console.log(lastLocation, lastHistory, action, history)
        // verify with last location to prevent duplicate action
        // if (action !== 'POP' && matchHash({ location: lastLocation, hash: nextLocation.hash, path: nextLocation.pathname })) {
        //   return
        // }
        const currentRouteData = retrieveRouteByLocation({ location: nextLocation, routes })
        if (!_.isEmpty(currentRouteData)) {
          i13nHistorylogger.debug('[History Listener] Route change, action:', action, lastLocation, nextLocation, 'route found:', currentRouteData)
          cb && cb(currentRouteData)
        } else {
          i13nHistorylogger.warn('[History Listener] - Route not found, please check again. + action:', action, 'input location: ', nextLocation, 'input routes: ', routes)
        }
      })
    }

    render () {
      return <WrappedComponent {...this.props} />
    }
  }
}

/**
 * full i13n component wrapper HOC,
 * with ECI13n and our own i13n methods
 */
export const withI13nWrapper = (Component) => {
  const I13nMethodsWrappedCompo = withI13nMethods(Component)

  const ECI13nWrapped = props => (
    <ECI13nWrapper
      component={I13nMethodsWrappedCompo}
      {...props}
    />
  )

  return ECI13nWrapped
}
